import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Avatar } from '@/components/Avatar/Avatar';
import { StatusSelect } from '@/components/StatusSelect/StatusSelect';
import { useStatusChange } from '@/components/StatusSelect/useStatusChange';
import Paper from '@/components/shared/Paper/Paper';
import { Spinner } from '@/components/shared/Spinner/Spinner';
import { Text } from '@/components/shared/Text/Text';
import { TextField } from '@/components/shared/TextField/TextField';
import { SessionStatus } from '@/lib/types/session-status';
import { formatTimeWithTimeZone } from '@/lib/utils/format-time-with-timezone';

import EmailCopy from './components/EmailCopyField/EmailCopyField';
import { InfoRow } from './components/InfoRow/InfoRow';
import SessionHandler from './components/SessionActions/SessionActions';
import { useSessionDetail } from './hooks/useSessionDetail';

const SessionView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const coachId = searchParams.get('coachId');

  const { data: session, isLoading, refetch } = useSessionDetail(id);

  const { handleStatusChange, selectedStatus, isUpdating } = useStatusChange(
    session ? [session] : [],
  );

  const fetchSession = () => {
    if (id) {
      refetch();
    }
  };

  if (isLoading || !session) {
    return <Spinner />;
  }

  return (
    <Paper className="w-[576px]">
      <Text.H5 className="mb-4" weight="bold">
        Session Info
      </Text.H5>
      <EmailCopy userEmail={session.user.email} />

      <InfoRow
        label="Coach"
        value={
          <div className="flex gap-4 items-center">
            <Avatar
              src={session.coach?.photoUrl ? session.coach?.photoUrl : '/icons/avatar.svg'}
              size={36}
            />
            <Text.Regular>{session?.coach?.name}</Text.Regular>
          </div>
        }
      />

      <InfoRow label="User timezone" value={session.user.timezone} />
      <InfoRow label="Coach timezone" value={session?.coach?.timezone} />
      <InfoRow
        label="Date (User)"
        value={`${formatTimeWithTimeZone(session.startsAt as string, session.user.timezone).date} - ${formatTimeWithTimeZone(session.endsAt as string, session.user.timezone).time}`}
      />
      <InfoRow
        label="Date (Coach)"
        value={`${formatTimeWithTimeZone(session.startsAt as string, session?.coach?.timezone).date} - ${formatTimeWithTimeZone(session.endsAt as string, session?.coach?.timezone).time}`}
      />
      {session.meet?.url && (
        <InfoRow
          label="Meeting URL"
          value={<Link to={session.meet.url}>{session.meet?.url}</Link>}
        />
      )}

      <InfoRow
        label="Status"
        value={
          <StatusSelect
            status={selectedStatus[session.id] || (session.status as SessionStatus)}
            onChange={(value) => handleStatusChange(session.id, value)}
            disabled={!!coachId || isUpdating}
          />
        }
      />

      <Text.H5 className="py-4" weight="bold">
        Session Summary
      </Text.H5>
      <Text.Regular className="mb-2" weight="bold">
        User request:
      </Text.Regular>

      <TextField
        placeholder="User note"
        isTextArea
        rows={5}
        className="p-0 mb-4"
        value={session.user.note}
        disabled={!!coachId}
      />

      <SessionHandler session={session} fetchSession={fetchSession} />
    </Paper>
  );
};

export default SessionView;
